import EditIcon from '@mui/icons-material/Edit';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import MailIcon from '@mui/icons-material/Mail';
import InboxOutlinedIcon from '@mui/icons-material/InboxOutlined';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import PlaylistAddOutlinedIcon from '@mui/icons-material/PlaylistAddOutlined';
import { alpha, Button, IconButton, Tooltip, Typography } from '@mui/material';
import Badge, { BadgeProps } from '@mui/material/Badge';
import { styled } from '@mui/material/styles';
import { Routes } from 'enums';
import { getCoPilotRouteForPathAndTab } from 'features/co-pilot/co-pilot-routes';
import { FunnelStatuses } from 'features/co-pilot/co-pilot.types';
import PaiLink from 'features/ui/base-components/pai-link';
import _ from 'lodash';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';
import useAppBarNavigationStore, { NavigationStates } from 'stores/app-bar-navigation-store';
import { makeStyles } from 'tss-react/mui';
import { formatDateTime } from 'utils/date.utils';
import CampaignItemMenu from './campaign-item-menu';
import { FunnelOverview, StatisticsAccordionStates } from './campaign-overview.types';
import CampaignStatus from './campaign-status';
import { getContactSupplyIndicator, getContactSupplyTooltip } from './contact-supply.logic';

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  '& .MuiBadge-badge': {
    right: -4,
    top: -4,
    minWidth: '18px',
    height: '18px',
    borderRadius: '9px',
    padding: '0 4px',
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 500,
    border: `2px solid ${theme.palette.background.paper}`,
  },
}));

interface Props {
  funnel: FunnelOverview;
  startFunnelLoading: boolean;
  isSelected: boolean;
  /** Gerade über den Wizard „Neue Kampagne“ gestartet: blauer Rahmen und Badge „NEU“. */
  isNew?: boolean;
  userRole: string;
  onSaveToGlobalStore?: (funnel: FunnelOverview) => void;

  handleOpenApproveMessagesDialog(funnelId: string): () => void;

  /** Kontakte freigeben (settings.approveContacts). */
  handleOpenApproveContactsDialog?(funnelId: string): () => void;

  handleOpenConfirmDeleteDialog(funnel: FunnelOverview): () => void;

  handleOpenRenameCampaignDialog(funnel: FunnelOverview): () => void;
  handleOpenScheduleDialog?(funnel: FunnelOverview): () => void;
  handleCancelSchedule?(funnel: FunnelOverview): () => void;

  handleOpenCloneCampaignDialog(funnel: FunnelOverview): () => void;

  handleStopFunnel(funnelId: string): () => void;

  handleEditFunnel(funnelId: string): () => void;

  handleRestartFunnel(funnelId: string): () => void;

  handleArchiveCampaign(funnel: FunnelOverview, archive: boolean): () => void;

  handleOpenEditCampaignLinkedinAccountDialog(funnel: FunnelOverview): () => void;

  mapLinkedinAccountIdToName(liAccountId: string): string;

  /** End of a temporary risk reduction on the campaign's account, if one is running. */
  riskReductionForAccount?(liAccountId: string): { until: Date; percent: number } | null;

  handleCampaignSelection(event: React.MouseEvent, campaignId: string): void;

  handleOpenCampaignDetailView(funnel: FunnelOverview, preSelect: StatisticsAccordionStates | null): void;
}

const CampaignItem: React.FC<Props> = ({
  funnel,
  startFunnelLoading,
  isSelected,
  isNew,
  userRole,
  onSaveToGlobalStore,
  handleOpenApproveMessagesDialog,
  handleOpenApproveContactsDialog,
  handleOpenConfirmDeleteDialog,
  handleOpenRenameCampaignDialog,
  handleOpenScheduleDialog,
  handleCancelSchedule,
  handleOpenCloneCampaignDialog,
  handleStopFunnel,
  handleEditFunnel,
  handleRestartFunnel,
  handleArchiveCampaign,
  handleOpenEditCampaignLinkedinAccountDialog,
  mapLinkedinAccountIdToName,
  riskReductionForAccount,
  handleCampaignSelection,
  handleOpenCampaignDetailView,
}) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const setAppBarNavigation = useAppBarNavigationStore((state) => state.setActiveTab);

  const [anchorEl, setAnchorEl] = useState<any | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);

  const handleOpenMenu = (el: any) => {
    setMenuOpen(true);
    setAnchorEl(el);
  };

  const handleCloseMenu = () => {
    setMenuOpen(false);
    setAnchorEl(null);
  };

  const { name, linkedinAccount, status, toBeApprovedMessages, toBeApprovedContacts = 0, _id, createDate } = funnel;

  const {
    totalContactsCount = 0,
    contactedContactsCount = 0,
    replyAndAcceptCount = 0,
  } = funnel.funnelSummary || {};

  const calculatePercentage = (current: number, total: number | undefined) => (total ? ((current / total) * 100).toFixed(2) : null);

  const renderPercentage = (current: number, total: number | undefined) =>
    !_.isNil(total) && total > 0 && <Typography className={classes.metricRate}>{calculatePercentage(current, total)} %</Typography>;

  // Kontaktvorrat neben der Lead-Zahl: Nachschub aus der Suche (blau) oder
  // wirklich leer (Warnfarbe), jeweils mit erklärendem Hovertext.
  const contactSupply = getContactSupplyIndicator(funnel.funnelSummary);
  const renderContactSupply = () => {
    if (!contactSupply) return null;
    const tooltip = getContactSupplyTooltip(contactSupply);
    const title = t(tooltip.key, { count: tooltip.count });
    const SupplyIcon = contactSupply.kind === 'more' ? PlaylistAddOutlinedIcon : InboxOutlinedIcon;
    return (
      <Tooltip title={title} arrow>
        <SupplyIcon
          className={cx(classes.supplyIcon, contactSupply.kind)}
          role={'img'}
          aria-label={title}
          data-testid={`contact-supply-${contactSupply.kind}`}
        />
      </Tooltip>
    );
  };

  const handleOpenFunnelStatistics = () => {
    const route = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.STATISTICS);
    if (route) {
      navigate(route);
    } else {
      setAppBarNavigation(NavigationStates.STATISTICS, false);
    }
    localStorage.setItem('preselect-funnel-statistics', _id);
  };

  const handleAccountManagementRedirect = (event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    const route = getCoPilotRouteForPathAndTab(location.pathname, NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT);
    if (route) {
      navigate(route);
    } else {
      setAppBarNavigation(NavigationStates.LINKEDIN_ACCOUNT_MANAGEMENT, false);
    }
  };

  return (
    <>
      <div
        className={cx(classes.card, (isSelected || isNew) && 'selected')}
        role={'button'}
        tabIndex={0}
        onClick={(event) => handleCampaignSelection(event, _id)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            handleCampaignSelection(event as unknown as React.MouseEvent, _id);
          }
        }}
      >
        {/* campaign name */}
        <div className={classes.colCampaign}>
          <div className={classes.campaignName}>
            <PaiLink
              text={_.truncate(name, { length: 50 })}
              size={'lg'}
              bold={true}
              variant={'primary'}
              underline={'hover'}
              IconComponent={<EditIcon />}
              iconPlacement={'end'}
              showIconOnHover={true}
              action={handleEditFunnel(_id)}
            />
            {isNew ? <span className={classes.newBadge}>{t('wizNewBadge')}</span> : null}
          </div>
          <Typography className={classes.metaLine}>
            {t('campaignCardMeta', {
              lead: mapLinkedinAccountIdToName(linkedinAccount),
              date: createDate ? formatDateTime(createDate, 'Pp') : '-',
            })}
          </Typography>
          {(() => {
            const rr = riskReductionForAccount?.(linkedinAccount);
            if (!rr) return null;
            const values = { date: formatDateTime(rr.until.toISOString(), 'P'), percent: rr.percent };
            return (
              <Tooltip title={t('riskReductionHint', values)} arrow>
                <Typography className={classes.metaLine} sx={{ color: 'warning.main' }}>
                  ⚠ {t('riskReductionLabel', values)}
                </Typography>
              </Tooltip>
            );
          })()}
        </div>

        {/* operational status */}
        <div className={classes.colStatus}>
          <CampaignStatus
            funnel={funnel}
            status={status}
            startFunnelLoading={startFunnelLoading}
            handleEditFunnel={handleEditFunnel}
            handleRestartFunnel={handleRestartFunnel}
            handleAccountManagementRedirect={handleAccountManagementRedirect}
            handleOpenEditCampaignLinkedinAccountDialog={handleOpenEditCampaignLinkedinAccountDialog}
            handleOpenApproveMessages={handleOpenApproveMessagesDialog(_id)}
            handleOpenApproveContacts={handleOpenApproveContactsDialog ? handleOpenApproveContactsDialog(_id) : undefined}
          />
        </div>

        {/* leads in campaign (total) + Kontaktvorrat aus der Suche */}
        <div className={cx(classes.colMetric, classes.metricValue, classes.metricWithSupply)}>
          {renderContactSupply()}
          {totalContactsCount}
        </div>

        {/* contacted */}
        <div className={cx(classes.colMetric, classes.metricValue)}>{contactedContactsCount}</div>

        {/* accepted / replied */}
        <div className={classes.colReplies}>
          <Typography className={classes.metricValue}>
            {replyAndAcceptCount}
            {contactedContactsCount > 0 ? <span className={classes.metricTotal}> / {contactedContactsCount}</span> : null}
          </Typography>
          {renderPercentage(replyAndAcceptCount, contactedContactsCount)}
        </div>

        {/* actions */}
        <div className={classes.colActions}>
          {toBeApprovedContacts > 0 && handleOpenApproveContactsDialog ? (
            <Tooltip title={t('approveContacts')}>
              <IconButton className={classes.approvalsButton} size={'small'} onClick={handleOpenApproveContactsDialog(_id)}>
                <StyledBadge badgeContent={toBeApprovedContacts} color="error">
                  <HowToRegIcon />
                </StyledBadge>
              </IconButton>
            </Tooltip>
          ) : null}
          {toBeApprovedMessages > 0 ? (
            <Tooltip title={t('approveMessages')}>
              <IconButton className={classes.approvalsButton} size={'small'} onClick={handleOpenApproveMessagesDialog(_id)}>
                <StyledBadge badgeContent={toBeApprovedMessages} color="error">
                  <MailIcon />
                </StyledBadge>
              </IconButton>
            </Tooltip>
          ) : null}
          <Button className={classes.detailsButton} onClick={() =>
              isNew
                ? navigate(`${Routes.RECRUITING_BOOSTER_CO_PILOT_NEW_CAMPAIGN}/${_id}?run=1`)
                : handleOpenCampaignDetailView(funnel, null)
            }
          >
            {t('details', 'Details')}
          </Button>
          <IconButton className={classes.menuButton} size={'small'} onClick={(event: React.MouseEvent) => handleOpenMenu(event.target)}>
            <MoreVertIcon className={classes.icon} />
          </IconButton>
        </div>
      </div>

      <CampaignItemMenu
        anchorEl={anchorEl}
        menuOpen={menuOpen}
        status={funnel.status}
        handleEditFunnel={handleEditFunnel(_id)}
        handleOpenRenameCampaignDialog={handleOpenRenameCampaignDialog(funnel)}
        handleOpenScheduleDialog={handleOpenScheduleDialog ? handleOpenScheduleDialog(funnel) : undefined}
        handleCancelSchedule={handleCancelSchedule ? handleCancelSchedule(funnel) : undefined}
        isScheduled={!!funnel.scheduledStartAt}
        handleOpenFunnelStatistics={status !== FunnelStatuses.CREATED ? handleOpenFunnelStatistics : undefined}
        handleOpenCloneCampaignDialog={handleOpenCloneCampaignDialog(funnel)}
        handleOpenConfirmDeleteDialog={handleOpenConfirmDeleteDialog(funnel)}
        handleArchiveCampaign={handleArchiveCampaign(funnel, true)}
        handleUnarchiveCampaign={handleArchiveCampaign(funnel, false)}
        handleStopFunnel={
          funnel.status !== FunnelStatuses.CREATED &&
            funnel.status !== FunnelStatuses.SUSPENDED &&
            funnel.status !== FunnelStatuses.FINISHED &&
            funnel.status !== FunnelStatuses.ARCHIVED
            ? handleStopFunnel(funnel._id)
            : undefined
        }
        handleRestartFunnel={funnel.status === FunnelStatuses.SUSPENDED ? handleRestartFunnel(funnel._id) : undefined}
        handleOpenEditCampaignLinkedinAccountDialog={
          funnel.status === FunnelStatuses.ACCOUNT_NOT_FOUND ? handleOpenEditCampaignLinkedinAccountDialog(funnel) : undefined
        }
        handleCloseMenu={handleCloseMenu}
        userRole={userRole}
        onSaveToGlobalStore={onSaveToGlobalStore ? () => onSaveToGlobalStore(funnel) : undefined}
      />
    </>
  );
};

const useStyles = makeStyles()((theme) => ({
  card: {
    background: '#FFF',
    borderRadius: '12px',
    padding: '20px 24px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    boxShadow: '0 1px 3px 0 rgba(0,0,0,0.06)',
    cursor: 'pointer',
    transition: 'box-shadow 150ms ease',
    '&:hover': {
      boxShadow: '0 4px 12px 0 rgba(0,0,0,0.12)',
    },
    '&.selected': {
      boxShadow: `0 0 0 1px ${theme.palette.primary.main}, 0 4px 12px 0 rgba(0,0,0,0.12)`,
    },
  },
  colCampaign: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  colStatus: {
    width: '200px',
    flexShrink: 0,
  },
  colMetric: {
    width: '120px',
    flexShrink: 0,
    textAlign: 'right',
  },
  colReplies: {
    width: '150px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: '4px',
  },
  colActions: {
    width: '132px',
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '8px',
  },
  newBadge: {
    fontSize: theme.typography.pxToRem(11),
    fontWeight: 700,
    color: theme.palette.primary.main,
    background: theme.palette.backgrounds.main,
    borderRadius: '6px',
    padding: '2px 6px',
  },
  campaignName: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    '& .MuiTypography-root': {
      fontSize: theme.typography.pxToRem(17),
      fontWeight: 500,
    },
  },
  metaLine: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  metricWithSupply: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: '6px',
  },
  supplyIcon: {
    fontSize: theme.typography.pxToRem(18),
    cursor: 'help',
    '&.more': {
      color: theme.palette.primary.main,
    },
    '&.exhausted': {
      color: theme.palette.warning.main,
    },
  },
  metricValue: {
    fontSize: theme.typography.pxToRem(20),
    fontWeight: 500,
    fontVariantNumeric: 'tabular-nums',
  },
  metricTotal: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: 400,
    color: theme.palette.greys.medium,
  },
  metricRate: {
    fontSize: theme.typography.pxToRem(13),
    color: theme.palette.greys.medium,
  },
  detailsButton: {
    height: '32px',
    padding: '0 14px',
    minWidth: 'auto',
    border: `1px solid ${alpha(theme.palette.primary.main, 0.5)}`,
    borderRadius: '8px',
    background: '#FFF',
    fontSize: theme.typography.pxToRem(13),
    fontWeight: 400,
    textTransform: 'none',
    color: theme.palette.primary.main,
    '&:hover': {
      background: theme.palette.backgrounds.main,
      borderColor: theme.palette.primary.main,
    },
  },
  approvalsButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    color: theme.palette.primary.main,
    background: theme.palette.backgrounds.main,
    '& .MuiSvgIcon-root': {
      fontSize: theme.typography.pxToRem(20),
      color: theme.palette.primary.main,
    },
    '&:hover': {
      background: theme.palette.backgrounds.main,
    },
  },
  menuButton: {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    color: theme.palette.greys.medium,
    '&:hover': {
      background: theme.palette.backgrounds.main_new,
      color: theme.palette.primary.main,
    },
  },
  icon: {
    fontSize: theme.typography.pxToRem(20),
    color: 'inherit',
    '&.success': {
      color: theme.palette.success.main,
    },
    '&.error': {
      color: theme.palette.error.main,
    },
    '&.warning': {
      color: theme.palette.warning.main,
    },
  },
  tooltipText: {
    fontSize: theme.typography.pxToRem(14),
  },
}));

export default CampaignItem;
